/**
 * Publishes ready Omnimizer deployments to the AI Catalog.
 *
 * The AI Catalog and the chat playground both list rag-agents LLM configs, so a
 * deployment becomes usable there once it has one. This builds the same
 * "private" config as RegisterModelForRagDialog (manual registration): the
 * deployment's OpenAI-compatible endpoint, a dedicated Omnimizer API key, and
 * external_id = deployment model_id. rag-agents enforces external_id
 * uniqueness, which makes publishing idempotent across tabs and retries.
 *
 * It runs in the browser of the deployment owner (OmnimizerCatalogSync is
 * mounted once in App.jsx), because both the key and the catalog entry belong
 * to that user and project.
 */
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import {
  buildRequestData,
  getInitialFormData,
} from "@/components/AICatalog/utils/catalog";
import { llmApi } from "@/services/rag-service";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjects } from "@/contexts/ProjectContext";
import { modelQueryKeys } from "@/features/playground/model-query-keys";
import { omnimizerApi, omnimizerKeys } from "./api";
import { deploymentState } from "./state";
import type { OmnimizerDeployment } from "./types";

export const CATALOG_AUTH_HEADER = "x-omnimize-api-key";
const INTENT_PREFIX = "omnimizer.catalogIntent.";
const RETRY_AFTER_MS = 5 * 60 * 1000;

export type CatalogIntent = { projectId: string; name: string; isPublic: boolean };

export type CatalogEntry = { id: string; name: string; external_id?: string | null };

export type CatalogStatus =
  | { state: "published"; entry: CatalogEntry }
  | { state: "publishing" }
  | { state: "waiting" }
  | { state: "needsProject" }
  | { state: "failed"; error: string }
  | { state: "notOwner" }
  | { state: "unsupported" };

export function saveCatalogIntent(deploymentId: string, intent: CatalogIntent) {
  try {
    localStorage.setItem(INTENT_PREFIX + deploymentId, JSON.stringify(intent));
  } catch {
    // Without storage the current project is used when the model is ready.
  }
}

export function readCatalogIntent(deploymentId: string): CatalogIntent | null {
  try {
    const raw = localStorage.getItem(INTENT_PREFIX + deploymentId);
    return raw ? (JSON.parse(raw) as CatalogIntent) : null;
  } catch {
    return null;
  }
}

function clearCatalogIntent(deploymentId: string) {
  try {
    localStorage.removeItem(INTENT_PREFIX + deploymentId);
  } catch {
    // Nothing to clean up.
  }
}

const isTextGeneration = (d: OmnimizerDeployment) => {
  const llm = (d.args?.llm || {}) as Record<string, unknown>;
  return d.args?.is_llm !== false && !llm.is_embedding;
};

export function catalogEligibility(
  d: OmnimizerDeployment,
  ownerId: string | undefined,
): "eligible" | "waiting" | "notOwner" | "unsupported" {
  if (!isTextGeneration(d)) return "unsupported";
  if (!ownerId || (d.user_id && d.user_id !== ownerId)) return "notOwner";
  if (!d.is_active || deploymentState(d) !== "ready" || !d.endpoint_url) return "waiting";
  return "eligible";
}

export function resolveIntent(
  d: OmnimizerDeployment,
  selectedProjects: string[],
): CatalogIntent | null {
  const stored = readCatalogIntent(d.model_id);
  if (stored && (stored.isPublic || stored.projectId)) return stored;
  const single =
    selectedProjects.length === 1 && selectedProjects[0] !== "all"
      ? selectedProjects[0]
      : "";
  return single ? { projectId: single, name: d.name, isPublic: false } : null;
}

// Same request shape as RegisterModelForRagDialog.transformRequestData.
export function buildCatalogRequest(
  d: OmnimizerDeployment,
  apiKey: string,
  intent: CatalogIntent,
) {
  const contextLength = d.args?.llm?.max_model_len ?? d.args?.dynamo?.max_model_len;
  const form = {
    ...getInitialFormData(intent.isPublic),
    name: intent.name || d.name,
    provider: "private",
    model: d.name,
    endpoint: d.endpoint_url || "",
    auth_header_type: CATALOG_AUTH_HEADER,
    api_key: apiKey,
    project_id: intent.projectId,
    is_public: intent.isPublic,
    context_length: contextLength ? String(contextLength) : "",
  };
  const request: Record<string, unknown> = {
    ...buildRequestData(form, intent.isPublic),
    provider: "private",
    endpoint: d.endpoint_url,
    auth_header_type: CATALOG_AUTH_HEADER,
    is_active: true,
    is_public: intent.isPublic,
    external_id: d.model_id,
  };
  request.extra_config = {
    ...(request.extra_config as Record<string, unknown>),
    api_key: apiKey,
  };
  if (intent.isPublic) delete request.project_id;
  else request.project_id = intent.projectId;
  return request;
}

export async function findCatalogEntry(deploymentId: string) {
  return (await llmApi.getLLMConfigByExternalId(deploymentId)) as CatalogEntry | null;
}

export async function publishDeployment(d: OmnimizerDeployment, intent: CatalogIntent) {
  const existing = await findCatalogEntry(d.model_id);
  if (existing) return existing;
  const key = await omnimizerApi.createApiKey(
    d.model_id,
    d.user_id || "",
    "Bunyan AI Catalog",
  );
  const request = buildCatalogRequest(d, key.key, intent);
  try {
    // rag-service.js types its parameters from JSDoc examples; the request is
    // the full LLM config payload built above.
    const created = (intent.isPublic
      ? await llmApi.createAdminLLMConfig(
          request as unknown as Parameters<typeof llmApi.createAdminLLMConfig>[0],
        )
      : await llmApi.createUserLLMConfig(
          request as unknown as Parameters<typeof llmApi.createUserLLMConfig>[0],
        )) as CatalogEntry;
    clearCatalogIntent(d.model_id);
    return created;
  } catch (error) {
    // A concurrent tab may have won the external_id race.
    const winner = await findCatalogEntry(d.model_id);
    if (winner) return winner;
    throw error;
  }
}

type SyncState = {
  inFlight: Record<string, true>;
  failures: Record<string, { at: number; error: string }>;
  start: (id: string) => void;
  finish: (id: string, error?: string) => void;
};

export const useCatalogSyncStore = create<SyncState>((set) => ({
  inFlight: {},
  failures: {},
  start: (id) => set((s) => ({ inFlight: { ...s.inFlight, [id]: true } })),
  finish: (id, error) =>
    set((s) => {
      const inFlight = { ...s.inFlight };
      delete inFlight[id];
      const failures = { ...s.failures };
      if (error) failures[id] = { at: Date.now(), error };
      else delete failures[id];
      return { inFlight, failures };
    }),
}));

export const catalogEntriesKey = ["omnimizer", "catalog-entries"] as const;

export function useCatalogEntries(enabled = true) {
  return useQuery({
    queryKey: catalogEntriesKey,
    queryFn: async () => {
      const options = { provider: "private", limit: 1000 } as unknown as Parameters<
        typeof llmApi.getLLMConfigs
      >[0];
      const response = (await llmApi.getLLMConfigs(options)) as unknown as
        | { items?: CatalogEntry[] }
        | CatalogEntry[];
      const items = Array.isArray(response) ? response : response?.items || [];
      return new Map(
        items.filter((i) => i.external_id).map((i) => [i.external_id as string, i]),
      );
    },
    enabled,
    staleTime: 15000,
  });
}

export function useOmnimizerDeployments(enabled = true, refetchInterval: number | false = 30000) {
  return useQuery({
    queryKey: omnimizerKeys.deployments,
    queryFn: omnimizerApi.listDeployments,
    enabled,
    refetchInterval,
  });
}

export function useCatalogStatuses(deployments: OmnimizerDeployment[]) {
  const { userProfile } = useAuth();
  const { canViewDeployments } = usePermissions();
  const { selectedProjects } = useProjects();
  const entries = useCatalogEntries(canViewDeployments);
  const { inFlight, failures } = useCatalogSyncStore();
  return useMemo(() => {
    const statuses = new Map<string, CatalogStatus>();
    for (const d of deployments) {
      const entry = entries.data?.get(d.model_id);
      const eligibility = catalogEligibility(d, userProfile?.uid);
      let status: CatalogStatus;
      if (entry) status = { state: "published", entry };
      else if (inFlight[d.model_id]) status = { state: "publishing" };
      else if (eligibility !== "eligible") status = { state: eligibility };
      else if (failures[d.model_id]) status = { state: "failed", error: failures[d.model_id].error };
      else if (!resolveIntent(d, selectedProjects)) status = { state: "needsProject" };
      else status = { state: "publishing" };
      statuses.set(d.model_id, status);
    }
    return statuses;
  }, [deployments, entries.data, failures, inFlight, selectedProjects, userProfile?.uid]);
}

const withLock = async (name: string, task: () => Promise<void>) => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return task();
  await locks.request(name, { ifAvailable: true }, async (lock) => {
    if (lock) await task();
  });
};

/** Mounted once: publishes the current user's ready deployments. */
export function OmnimizerCatalogSync() {
  const { userProfile } = useAuth();
  const { canViewDeployments } = usePermissions();
  const { selectedProjects } = useProjects();
  const ownerId = userProfile?.uid as string | undefined;
  const enabled = Boolean(ownerId) && canViewDeployments;
  const deployments = useOmnimizerDeployments(enabled);
  const entries = useCatalogEntries(enabled);
  const queryClient = useQueryClient();
  const { start, finish } = useCatalogSyncStore();

  useEffect(() => {
    if (!enabled || !deployments.data || !entries.data) return;
    const { inFlight, failures } = useCatalogSyncStore.getState();
    for (const d of deployments.data) {
      if (entries.data.has(d.model_id) || inFlight[d.model_id]) continue;
      if (catalogEligibility(d, ownerId) !== "eligible") continue;
      const failure = failures[d.model_id];
      if (failure && Date.now() - failure.at < RETRY_AFTER_MS) continue;
      const intent = resolveIntent(d, selectedProjects);
      if (!intent) continue;
      start(d.model_id);
      void withLock(`omnimizer-catalog:${d.model_id}`, async () => {
        try {
          await publishDeployment(d, intent);
          finish(d.model_id);
        } catch (error) {
          finish(d.model_id, error instanceof Error ? error.message : String(error));
        }
        await queryClient.invalidateQueries({ queryKey: catalogEntriesKey });
        await queryClient.invalidateQueries({ queryKey: modelQueryKeys.lists() });
      }).finally(() => {
        if (useCatalogSyncStore.getState().inFlight[d.model_id]) finish(d.model_id);
      });
    }
  }, [deployments.data, enabled, entries.data, finish, ownerId, queryClient, selectedProjects, start]);

  return null;
}
